import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MyBookItemStruct } from '../../../shared/services/structs/my-books.struct';
import { AdminReviewerBookService } from '../../../shared/services/apis/platform/admin-reviewer-book.service';
import { AdminReviewerBookResponse } from '../../../shared/services/responses/platform/admin-reviewer-book.response';
import { AdminReviewerBookRequest } from '../../../shared/services/requests/platform/admin-reviewer-book.request';

@Component({
    selector: 'app-admin-reviewer',
    standalone: true,
    templateUrl: './admin-reviewer.component.html',
    styleUrl: './admin-reviewer.component.css',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MenuComponent,
        FooterComponent,
        LoginValidationComponent,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatSelectModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminReviewerComponent implements OnInit {
    isLoading = false;

    form!: FormGroup;

    readonly statusOptions = [
        { label: 'Todos', value: null },
        { label: 'Em edição', value: 'Em edição' },
        { label: 'Aguarda pagamento', value: 'Aguarda pagamento' },
        { label: 'Em revisão', value: 'Em revisão' },
        { label: 'Aguarda ajustes', value: 'Aguarda ajustes' },
        { label: 'Aguarda aprovação', value: 'Aguarda aprovação' },
        { label: 'Publicado', value: 'Publicado' },
        { label: 'Suspenso', value: 'Suspenso' },
    ];

    list: MyBookItemStruct[] = [];
    page = 1;
    readonly pageSize = 100;
    total = 0;

    constructor(
        private fb: FormBuilder,
        private adminReviewerBookService: AdminReviewerBookService,
        private alertService: AlertService,
        private cdr: ChangeDetectorRef,
        private router: Router
    ) { }

    ngOnInit(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        this.form = this.fb.group({
            search: [''],
            status: [null],
            dateStart: [null],
            dateEnd: [null],
        });
    }

    onLoginValidated(isLogged: boolean) {
        if (!isLogged) return;
        this.fetch();
    }

    fetch(): void {
        this.isLoading = true;

        const req: AdminReviewerBookRequest = {
            page: this.page,
            pageSize: this.pageSize,
            search: (this.form.value.search || '').trim() || null,
            status: this.form.value.status || null,
            dateStart: this.toIsoOrNull(this.form.value.dateStart),
            dateEnd: this.toIsoOrNull(this.form.value.dateEnd),
        };

        this.adminReviewerBookService.postAdminReviewerBook(req).subscribe({
            next: (res: AdminReviewerBookResponse) => {
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Falha ao carregar livros.', AlertType.error);
                    this.isLoading = false;
                    this.cdr.markForCheck();
                    return;
                }

                const PLACEHOLDER = 'assets/images/book-placeholder.svg';
                this.list = (res.list ?? []).map(item => ({
                    ...item,
                    versions: item.versions?.map(v => ({
                        ...v,
                        coverUrl: (v.coverUrl && v.coverUrl.length ? v.coverUrl : PLACEHOLDER)
                    })) ?? []
                }));

                this.total = res.total ?? this.list.length;
                this.isLoading = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.alertService.show('Erro', 'Erro inesperado ao carregar a lista.', AlertType.error);
                this.isLoading = false;
                this.cdr.markForCheck();
            }
        });
    }

    applyFilters(): void {
        this.page = 1;
        this.fetch();
    }

    resetFilters(): void {
        this.form.reset({ search: '', status: null, dateStart: null, dateEnd: null });
        this.page = 1;
        this.fetch();
    }

    prevPage(): void {
        if (this.page <= 1) return;
        this.page--;
        this.fetch();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    nextPage(): void {
        if (this.page >= this.totalPages) return;
        this.page++;
        this.fetch();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.total / this.pageSize));
    }

    get pageRangeStart(): number {
        return (this.page - 1) * this.pageSize + 1;
    }

    get pageRangeEnd(): number {
        return Math.min(this.page * this.pageSize, this.total);
    }

    onImgError(ev: Event) {
        const el = ev?.target as HTMLImageElement;
        if (el) el.src = 'assets/images/book-placeholder.png';
    }

    trackByVersion = (_: number, v: any) => v?.idBookVersion ?? _;
    trackByBook = (_: number, b: any) => b?.idBook ?? _;

    goTo(idBookVersion: number) {
        if (!idBookVersion) return;
        this.router.navigateByUrl(`/revisor/${idBookVersion}`);
    }

    private toIsoOrNull(date: Date | null): string | null {
        if (!date) return null;
        return new Date(date).toISOString();
    }
}
